import { useEffect, useState, useCallback } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { ChevronLeft, ChevronRight, X, Maximize } from "lucide-react";
import { analysesApi, projectsApi } from "@/lib/api";
import Logo from "@/components/Logo";
import {
  DebtCompositionDonut, DebtGdpTrajectory,
  RiskGauge, EspaceSocialArea, OddScatter,
} from "@/components/charts/Charts";

export default function PresentationMode() {
  const { projectId } = useParams();
  const nav = useNavigate();
  const [slide, setSlide] = useState(0);
  const [data, setData] = useState(null);
  const [project, setProject] = useState(null);

  useEffect(() => {
    Promise.all([analysesApi.summary(projectId), projectsApi.get(projectId)]).then(([s, p]) => {
      setData(s); setProject(p);
    });
  }, [projectId]);

  const totalSlides = 8;
  const next = useCallback(() => setSlide((s) => Math.min(s + 1, totalSlides - 1)), []);
  const prev = useCallback(() => setSlide((s) => Math.max(s - 1, 0)), []);
  const close = useCallback(() => nav(`/app/projects/${projectId}`), [nav, projectId]);

  useEffect(() => {
    const onKey = (e) => {
      if (e.key === "ArrowRight" || e.key === "PageDown" || e.key === " ") { e.preventDefault(); next(); }
      if (e.key === "ArrowLeft" || e.key === "PageUp") { e.preventDefault(); prev(); }
      if (e.key === "Escape") close();
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [next, prev, close]);

  const goFullscreen = () => {
    const el = document.documentElement;
    if (!document.fullscreenElement) el.requestFullscreen?.();
    else document.exitFullscreen?.();
  };

  if (!data || !project) {
    return <div className="min-h-screen grid place-items-center bg-[#0D1B2A] text-white label-mono text-xs">Préparation des slides…</div>;
  }

  const slides = [
    <SlideCover key="0" project={project} />,
    <SlideKpis key="1" data={data} project={project} />,
    <SlideComposition key="2" data={data} />,
    <SlideService key="3" data={data} />,
    <SlideVitaliste key="4" data={data} project={project} />,
    <SlideConditionnalites key="5" data={data} />,
    <SlideOdd key="6" data={data} />,
    <SlideRecos key="7" data={data} project={project} />,
  ];

  return (
    <div className="fixed inset-0 bg-[#0D1B2A] text-white z-50 flex flex-col" data-testid="presentation-mode">
      {/* Slide content */}
      <div className="flex-1 overflow-hidden flex items-center justify-center p-8 md:p-16">
        <div className="w-full max-w-6xl reveal-up" key={slide}>
          {slides[slide]}
        </div>
      </div>

      {/* Controls */}
      <div className="border-t border-white/10 px-6 py-3 flex items-center justify-between bg-[#0A1520]">
        <div className="flex items-center gap-4">
          <Logo size={30} withText={false} />
          <span className="label-mono text-[10px] text-white/60">MODE PRÉSENTATION</span>
        </div>
        <div className="flex items-center gap-3">
          <button onClick={prev} disabled={slide === 0}
                  className="h-9 w-9 grid place-items-center rounded-sm bg-white/5 hover:bg-white/15 disabled:opacity-30 transition"
                  data-testid="slide-prev-btn" aria-label="Précédent">
            <ChevronLeft size={16} />
          </button>
          <span className="label-mono text-xs text-white/80 font-mono tabular-nums">
            {String(slide + 1).padStart(2, "0")} / {totalSlides}
          </span>
          <button onClick={next} disabled={slide === totalSlides - 1}
                  className="h-9 w-9 grid place-items-center rounded-sm bg-white/5 hover:bg-white/15 disabled:opacity-30 transition"
                  data-testid="slide-next-btn" aria-label="Suivant">
            <ChevronRight size={16} />
          </button>
        </div>
        <div className="flex items-center gap-2">
          <button onClick={goFullscreen} className="h-9 w-9 grid place-items-center rounded-sm bg-white/5 hover:bg-white/15 transition"
                  data-testid="slide-fullscreen-btn" aria-label="Plein écran">
            <Maximize size={14} />
          </button>
          <button onClick={close} className="h-9 w-9 grid place-items-center rounded-sm bg-white/5 hover:bg-alert transition"
                  data-testid="slide-close-btn" aria-label="Fermer">
            <X size={16} />
          </button>
        </div>
      </div>
    </div>
  );
}

function SlideCover({ project }) {
  return (
    <div>
      <div className="label-mono text-[11px] text-[#C9A84C]">DEBT-ANALYZER PRO · ANALYSE SOUVERAINE</div>
      <h1 className="mt-8 font-serif font-black text-6xl md:text-7xl leading-[0.9] tracking-tight">
        {project.name}
      </h1>
      <div className="mt-8 flex flex-wrap gap-x-10 gap-y-3 font-mono text-sm">
        <span><span className="text-white/50">Pays </span>{project.country}</span>
        <span><span className="text-white/50">Période </span>{project.period_start}–{project.period_end}</span>
        <span><span className="text-white/50">Devise </span>{project.currency}</span>
      </div>
      <blockquote className="mt-16 font-serif italic text-2xl border-l-2 border-[#C9A84C] pl-8 max-w-2xl">
        « Chaque dollar emprunté est une vie hypothéquée. »
        <div className="mt-3 label-mono text-[10px] text-white/60 not-italic">— A. ELY MUSTAPHA</div>
      </blockquote>
    </div>
  );
}

function SlideKpis({ data, project }) {
  const v = data.macro.viabilite || {};
  const vit = data.vitaliste || {};
  const kpis = [
    { l: "DETTE / PIB", v: v.debt_gdp_ratio, u: "%", b: v.debt_gdp_band },
    { l: "SERVICE / RECETTES", v: v.service_revenue_ratio, u: "%", b: v.service_revenue_band },
    { l: "TAVD (V5)", v: vit.V5, u: "%", b: vit.tavd_band },
    { l: "D2 — JOURS / CONTRIB.", v: vit.D2, u: "j" },
    { l: "V6 — ENFANT ENDETTÉ", v: vit.V6, u: "j" },
    { l: "IPSD", v: vit.IPSD, u: "%" },
  ];
  return (
    <div>
      <div className="label-mono text-[11px] text-[#C9A84C]">SLIDE 02 — KPIS</div>
      <h2 className="mt-2 font-serif font-bold text-5xl">Indicateurs clés</h2>
      <div className="mt-10 grid grid-cols-2 md:grid-cols-3 gap-5">
        {kpis.map((k) => (
          <div key={k.l} className="border border-white/15 p-6 rounded-sm">
            <div className="label-mono text-[9px] text-white/60">{k.l}</div>
            <div className={`mt-3 font-mono tabular-nums text-4xl font-semibold ${bandTextClass(k.b)}`}>
              {fmt(k.v)} <span className="text-xs text-white/40">{k.u}</span>
            </div>
            {k.b && <div className="mt-2 label-mono text-[9px] text-white/70">{k.b.replace("_", " ").toUpperCase()}</div>}
          </div>
        ))}
      </div>
    </div>
  );
}

function SlideComposition({ data }) {
  return (
    <div>
      <div className="label-mono text-[11px] text-[#C9A84C]">SLIDE 03 — COMPOSITION</div>
      <h2 className="mt-2 font-serif font-bold text-5xl">Composition & trajectoire</h2>
      <div className="mt-8 grid lg:grid-cols-2 gap-8 bg-white rounded-sm p-8 text-black">
        <div>
          <h3 className="font-serif font-bold">Composition de la dette</h3>
          <DebtCompositionDonut composition={data.macro.composition} />
        </div>
        <div>
          <h3 className="font-serif font-bold">Trajectoire dette / PIB</h3>
          <DebtGdpTrajectory currentDebtGdp={data.macro.viabilite?.debt_gdp_ratio} projection={data.macro.projection_5y} />
        </div>
      </div>
    </div>
  );
}

function SlideService({ data }) {
  return (
    <div>
      <div className="label-mono text-[11px] text-[#C9A84C]">SLIDE 04 — SERVICE</div>
      <h2 className="mt-2 font-serif font-bold text-5xl">Service & espace social</h2>
      <div className="mt-8 grid lg:grid-cols-3 gap-6">
        <div className="lg:col-span-1 border border-white/15 p-6 rounded-sm">
          <h3 className="font-serif font-bold text-lg">Indice de risque</h3>
          <div className="mt-4 bg-white rounded-sm p-4 text-black">
            <RiskGauge value={computeRisk(data.macro.viabilite, data.vitaliste)} />
          </div>
        </div>
        <div className="lg:col-span-2 bg-white rounded-sm p-6 text-black">
          <h3 className="font-serif font-bold">Espace budgétaire social</h3>
          <EspaceSocialArea macro={data.macro.macro_snapshot} debt={data.macro.debt_snapshot} vit={data.vitaliste} />
        </div>
      </div>
    </div>
  );
}

function SlideVitaliste({ data, project }) {
  const v = data.vitaliste || {};
  const year = project.period_end;
  return (
    <div>
      <div className="label-mono text-[11px] text-[#C9A84C]">SLIDE 05 — VITALISTE</div>
      <h2 className="mt-2 font-serif font-bold text-5xl">La dette en <em className="text-[#C0392B] not-italic">vies humaines</em></h2>
      <div className="mt-8 grid grid-cols-2 md:grid-cols-4 gap-4">
        {[
          ["D1", "DETTE / CONTRIB.", v.D1, project.currency],
          ["D2", "JOURS HYPOTHÉQUÉS", v.D2, "j"],
          ["D4", "CARRIÈRE ALIÉNÉE", v.D4, "%"],
          ["V5", "TAVD", v.V5, "%"],
        ].map(([code, l, value, u]) => (
          <div key={code} className="border-l-4 border-[#C9A84C] pl-5">
            <div className="font-mono text-[#C9A84C] text-sm">{code}</div>
            <div className="label-mono text-[9px] text-white/60 mt-1">{l}</div>
            <div className="mt-2 font-mono text-3xl">{fmt(value)} <span className="text-sm text-white/40">{u}</span></div>
          </div>
        ))}
      </div>
      {v.V6 != null && (
        <div className="mt-10 border border-[#C0392B]/50 bg-[#C0392B]/10 p-8 rounded-sm max-w-3xl">
          <div className="label-mono text-[10px] text-[#C0392B]">V6 — HÉRITAGE INTERGÉNÉRATIONNEL</div>
          <p className="mt-4 font-serif text-2xl italic">
            « Un enfant né en <strong className="not-italic">{year}</strong> en <strong className="not-italic">{project.country}</strong> hérite de{" "}
            <strong className="text-[#E74C3C] not-italic">{Math.round(v.V6)} jours</strong> de vie hypothéqués dès sa naissance, sans pouvoir consentir. »
          </p>
        </div>
      )}
    </div>
  );
}

function SlideConditionnalites({ data }) {
  const cond = data.aggregated_conditionalities || [];
  return (
    <div>
      <div className="label-mono text-[11px] text-[#C9A84C]">SLIDE 06 — CONDITIONNALITÉS</div>
      <h2 className="mt-2 font-serif font-bold text-5xl">Conditionnalités & impact social</h2>
      <div className="mt-8 bg-white rounded-sm p-6 text-black">
        <OddScatter conditionalities={cond} />
      </div>
      <p className="mt-6 text-sm text-white/70 max-w-3xl">
        {data.clauses?.count || 0} clause(s) extraite(s) · {data.clauses?.critical_count || 0} critique(s) ·
        Les coupes directes dans les dépenses sociales déclenchent l'alerte vitale.
      </p>
    </div>
  );
}

function SlideOdd({ data }) {
  const matrix = data.odd?.matrix || {};
  const LABELS = {
    ODD1: "Pauvreté", ODD2: "Faim", ODD3: "Santé", ODD4: "Éducation",
    ODD6: "Eau", ODD8: "Travail", ODD10: "Inégalités", ODD16: "Justice",
  };
  return (
    <div>
      <div className="label-mono text-[11px] text-[#C9A84C]">SLIDE 07 — IMPACT ODD</div>
      <h2 className="mt-2 font-serif font-bold text-5xl">Impact développemental</h2>
      <div className="mt-10 grid grid-cols-2 md:grid-cols-4 gap-4">
        {Object.entries(LABELS).map(([k, l]) => {
          const cell = matrix[k];
          const color = {
            rouge: "#C0392B", orange: "#E67E22", neutre: "#64748B", vert: "#27AE60",
          }[cell?.color] || "#334155";
          return (
            <div key={k} className="border border-white/15 rounded-sm overflow-hidden">
              <div style={{ background: color, height: 6 }} />
              <div className="p-4">
                <div className="label-mono text-[9px] text-white/60">{k}</div>
                <div className="mt-1 font-serif font-bold">{l}</div>
                <div className="mt-2 text-xs text-white/70">
                  {cell ? `Impact ${cell.color.toUpperCase()} · ${cell.occurrences} occ.` : "Non identifié"}
                </div>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}

function SlideRecos({ data, project }) {
  const recs = data.aggregated?.key_recommendations || [];
  const risks = data.aggregated?.key_risks || [];
  return (
    <div>
      <div className="label-mono text-[11px] text-[#C9A84C]">SLIDE 08 — CONCLUSION</div>
      <h2 className="mt-2 font-serif font-bold text-5xl">Recommandations</h2>
      <div className="mt-8 grid md:grid-cols-2 gap-8">
        <div>
          <div className="label-mono text-[10px] text-[#C0392B]">RISQUES CLÉS</div>
          <ul className="mt-3 space-y-2 text-sm">
            {(risks.length ? risks : ["Données de risques non extraites"]).slice(0, 6).map((r, i) => (
              <li key={i} className="flex gap-3 border-l-2 border-[#C0392B] pl-4 py-1">{r}</li>
            ))}
          </ul>
        </div>
        <div>
          <div className="label-mono text-[10px] text-[#27AE60]">RECOMMANDATIONS</div>
          <ul className="mt-3 space-y-2 text-sm">
            {(recs.length ? recs : ["Analyse à enrichir avec d'autres rapports"]).slice(0, 6).map((r, i) => (
              <li key={i} className="flex gap-3 border-l-2 border-[#27AE60] pl-4 py-1">{r}</li>
            ))}
          </ul>
        </div>
      </div>
      <div className="mt-16 pt-6 border-t border-white/10 text-xs text-white/50 flex justify-between">
        <span>{project.name} · {project.country} · {project.period_end}</span>
        <span>Méthodologie : A. ELY Mustapha — Théorie Nouvelle des Finances Publiques</span>
      </div>
    </div>
  );
}

function bandTextClass(band) {
  return {
    faible: "text-[#27AE60]", soutenable: "text-[#27AE60]",
    modere: "text-[#E67E22]", sous_tension: "text-[#E67E22]",
    eleve: "text-[#C0392B]", critique: "text-[#C0392B]",
    detresse: "text-[#E74C3C]", insoutenable: "text-[#E74C3C]",
  }[band] || "text-white";
}

function fmt(v) {
  if (v == null || Number.isNaN(v)) return "—";
  if (typeof v !== "number") return v;
  if (Math.abs(v) >= 1e9) return (v / 1e9).toFixed(2) + " B";
  if (Math.abs(v) >= 1e6) return (v / 1e6).toFixed(2) + " M";
  return v.toLocaleString("fr-FR", { maximumFractionDigits: 1 });
}

function computeRisk(v, vit) {
  let score = 0, n = 0;
  if (v?.debt_gdp_ratio != null) { score += Math.min(100, (v.debt_gdp_ratio / 70) * 60); n++; }
  if (v?.service_revenue_ratio != null) { score += Math.min(100, (v.service_revenue_ratio / 25) * 80); n++; }
  if (vit?.V5 != null) { score += Math.min(100, (vit.V5 / 400) * 90); n++; }
  if (vit?.IPSD != null) { score += Math.min(100, (vit.IPSD / 50) * 70); n++; }
  return n ? score / n : 0;
}
